/**
 * Rubric data + dimension-presentation helpers — the single home for the
 * dimension rubric text, the "Lx:" line parser, and the kebab→title-case
 * dimension formatter. Both <DimensionCard> and <DimensionRubricDialog> read
 * from here instead of importing rubric data across components.
 */

/** Words that should render fully uppercased rather than title-cased. */
const ACRONYMS = new Set(['ai']);

/** Dimension keys whose display label diverges from the generic kebab→title-case path. */
const DIMENSION_LABEL_OVERRIDES: Record<string, string> = {
  'framework-detection': 'Process Consistency',
};

/** Format dimension name from kebab-case to title case (acronyms uppercased). */
export function formatDimension(dim: string): string {
  if (dim in DIMENSION_LABEL_OVERRIDES) {
    return DIMENSION_LABEL_OVERRIDES[dim] ?? dim;
  }
  return dim
    .split('-')
    .map((w) =>
      ACRONYMS.has(w.toLowerCase())
        ? w.toUpperCase()
        : w.charAt(0).toUpperCase() + w.slice(1),
    )
    .join(' ');
}

/**
 * Rubric descriptions for each dimension.
 *
 * FR-7 AC-7.2: practice-neutral wording mirrored from the evaluator rubric
 * (see `lambdas/bedrock-evaluator/src/evaluate.ts` `DIMENSION_RUBRICS`).
 * Tooltip text MUST stay in sync with the evaluator so users see the same
 * language the LLM does — and MUST avoid framework-specific filenames
 * (CLAUDE.md, .speads, .beads, BMAD, etc.). Judge the practice, not the
 * filename.
 */
export const DIMENSION_RUBRICS: Record<string, string> = {
  'constitution-maturity':
    'Judged on the PRACTICE, not any one filename — an AI-instructions file (e.g. CLAUDE.md or AGENTS.md) and a framework-style constitution.md are both "rules artifacts" and count equally, whichever convention the repo actually uses (including both at once). A repo is never scored higher for having multiple such files — content quality and evolution are what move the level.\n' +
    'L1: No agent-facing rules — no written behavioural guidance beyond source code itself.\n' +
    'L2: Generic/boilerplate rules — copy-pasted template content not tied to this codebase.\n' +
    "L3: Project-specific rules — named technologies and concrete do/don't examples drawn from the actual repo.\n" +
    'L4: Detailed and actionable — MUST/SHALL imperatives, thresholds, validation steps, and documented anti-patterns across concern areas; the rule text has been refined over time.\n' +
    'L5: Sustained — versioned amendments with rationale and a change history kept current; a single self-contained rules file with no hierarchy and no enforcement can reach L5. Enforcement (hooks, CI, coverage gates), hierarchy/inheritance, and adherence are optional bonus signals — credited when present, never required.',
  'artifact-traceability':
    'L1: No recorded intent — code lands with no requirement, ticket, or design trail.\n' +
    'L2: Intent is recorded somewhere (an issue, a doc, a ticket) but unlinked — you cannot walk from a commit back to the requirement.\n' +
    'L3: Some code links back to intent — some commits or PRs reference an issue/ticket; the chain is partial or inconsistent across features.\n' +
    'L4: A complete, walkable chain consistent across recent features, linked at the level the team lands work (PR → issue or bundle-commit → spec); a task ID is a pointer to the requirement, not a per-commit stamp, and external-only linkage reaches L4.\n' +
    'L5: In-repo AC-verified evidence — proof the acceptance criteria were met (a conformance verdict, a coverage/analyze table, a verify report, or a CI test-gate wired onto AC-bearing work), repeatable across recent features; external-only linkage never reaches L5.',
  'supplemental-context':
    'L1: No supplemental context — neither local docs nor declared references; only source code and at most a minimal README.\n' +
    'L2: Basic README with usage/setup but nothing AI- or workflow-specific, and no declared external references — slash commands and skills folders are tooling, not supplemental context, so a repo with only those stays at L1/L2.\n' +
    'L3: Some explanatory content — a substantive README or architecture doc, an onboarding doc, a domain glossary, decision records, or a few declared external references; partial, single-source, or inconsistently maintained.\n' +
    'L4: Well-structured context with breadth — multiple reusable explanatory assets, authored locally or adopted by reference (declared constitution hierarchy levels, remote references, or context-pack manifests count equally with locally authored docs), clearly organized and linked.\n' +
    'L5: Comprehensive ecosystem — layered docs and/or declared hierarchies, decision records, and reusable context packs that together give the AI what it needs, with evidence the context is actively cited in commits and PRs.',
  'feedback-loop':
    'Credited via WHATEVER mechanism the team actually uses — PR review, CI/automated verification, issue follow-ups, incident/postmortem/retro docs, OR rule/constitution/template/check amendments. None of these is required over the others; a team that closes the loop entirely through versioned constitution amendments scores the same as one using retro docs.\n' +
    'L1: No evidence of any feedback mechanism — no PR reviews, no CI, no follow-up issues, no incident/retro/lessons docs, no rule or constitution amendments.\n' +
    'L2: Feedback activity happens (PRs are reviewed, CI runs, issues are filed) but does not feed back into the process — no rule/constitution amendments or tracked follow-up actions after features ship.\n' +
    'L3: Feedback activity AND occasional improvement — a rule/constitution amendment, template/check added, or a tracked action item resolved, after a feature or incident.\n' +
    'L4: Regular feedback cycles across recent work via whatever the team uses (consistent PR review, CI verification, retrospectives, issue follow-ups, OR rule/constitution amendments) AND learnings drive process improvements.\n' +
    'L5: Systematic and continuous — improvements are common and traceable to concrete lessons across recent delivery, through any combination of these channels, including a constitution/rules-file amendment history whose entries cite the lesson that prompted them.',
  'ai-collaboration':
    'L1: No AI-assisted development evidence — no provenance, no role documentation, no conventions.\n' +
    'L2: Occasional AI-involvement markers (co-author trailers, inline credit), but inconsistent and undocumented.\n' +
    'L3: Regular AI-involvement markers with temporal pattern — you can tell which commits involved an AI.\n' +
    'L4: Consistent provenance on AI-assisted commits, clear human/AI role separation in the rules, documented handoff patterns.\n' +
    'L5: Exemplary collaboration — full provenance, ownership descriptions, escalation paths, conventions evolving in git history.',
  'framework-detection':
    'L1: No repeatable pattern — every feature is delivered ad-hoc with no shared artifact or workflow.\n' +
    "L2: Some features follow a pattern, others don't — inconsistent application of process.\n" +
    'L3: Most features follow a similar process — manual templates, checklists, or a named framework all acceptable.\n' +
    "L4: Every feature follows the same process consistently across the last 3-5 features — process is the team's default.\n" +
    'L5: Process is self-reinforcing (templates/tooling/automation) and evolves with feedback; the team improves the process itself.',
  // Discovery dimensions
  'Discovery Planning Maturity':
    'L1: Ad-hoc — no defined scope, success metrics, or timelines before discovery starts.\n' +
    'L2: Basic awareness — some features have rough scope/timelines but practice is inconsistent.\n' +
    'L3: Consistent — scope, success metrics, and timelines defined before discovery; documented approach applied across features.\n' +
    'L4: Data-driven — planning artifacts reference historical learnings; scope and metrics are evidence-based.\n' +
    'L5: Continuously improving — planning templates evolve via retrospectives; scope accuracy is tracked over time.',
  'Problem Framing Quality':
    'L1: Absent — features propose solutions with no documented problem, user need, or current-state pain.\n' +
    'L2: Vague or fused — problem statement exists BUT is generic, OR conflates problem with solution, OR omits affected users, OR asserts impact without articulation.\n' +
    'L3: Consistent — ALL THREE: clear user-pain statement, problem distinct from solution, named users + impact dimensions; root causes identified though not always validated.\n' +
    'L4: Evidence-based — problem quantified with operational or user-research data about the problem itself (defect rates, complaint volumes, interview findings); prioritization scores like Business Impact ratings do NOT count as problem quantification.\n' +
    'L5: Systematic — teams iterate on problem statements based on research; reusable framing patterns maintained.',
  'Hypothesis Validation Rigor':
    'L1: None — features proceed from idea to implementation; assumptions are never surfaced or tested.\n' +
    'L2: Occasional — some features articulate hypotheses but validation is rare and informal.\n' +
    'L3: Regular — hypotheses documented with validation criteria; key assumptions tested via structured methods.\n' +
    'L4: Data-driven — success/failure criteria defined; failed hypotheses documented and feed into future cycles.\n' +
    'L5: Systematic — hypothesis library maintained; validation methods improve via effectiveness retrospectives.',
  'Solution Option Exploration':
    'L1: None — ZERO alternatives mentioned. Phasing of the same approach is scope sequencing, NOT exploration. Phrases like "lowest risk path" without naming the comparison stay at L1.\n' +
    'L2: Mentioned only — at least one alternative named (even briefly) but no documented trade-off analysis, comparison criteria, or decision rationale.\n' +
    'L3: Consistent — multiple options documented AND each evaluated against stated criteria (effort, risk, user value); chosen-option rationale links back to those criteria.\n' +
    'L4: Evidence-based — options assessed against measurable criteria (cost data, feasibility prototypes, user research) — not just team intuition.\n' +
    'L5: Systematic — reusable evaluation frameworks maintained; past decisions inform future option exploration.',
  'User Validation Depth':
    'L1: None — no user validation during discovery; assumptions about user needs never tested.\n' +
    'L2: Occasional — informal user validation (single test or stakeholder review); rarely influences direction.\n' +
    'L3: Regular — structured user feedback (interviews, usability tests, surveys) meaningfully shapes solutions.\n' +
    'L4: Systematic — recruitment criteria, sample sizes, and success metrics defined; findings tracked and drive decisions.\n' +
    'L5: Continuously improving — validated personas and research repositories maintained; methods evolve over time.',
  'Discovery-to-Delivery Handoff':
    'L1: None — feature is unstructured prose; ZERO of the 7 handoff artifacts are present.\n' +
    'L2: 1-2 artifacts — some concrete guidance (e.g., 1 AC + DEV note); scope, dependencies, and edge cases are still left for the team to work out.\n' +
    'L3: 3+ artifacts — multi-AC list, explicit in/out-of-scope, dependency table with owner/status, attached prototype, architecture spec, open questions log, success metrics. Implementation team can start without re-deriving.\n' +
    'L4: Measured — meets L3 bar AND observable feedback loop: delivery questions traced to discovery gaps are logged, retrospectives or rework metrics referenced. Rich artifacts without measurement do NOT reach L4.\n' +
    'L5: Continuously improving — delivery teams provide systematic feedback that improves future discovery artifact quality.',
};

/**
 * Display-only dimension weights, as integer percent of the overall score.
 * Mirrors the two scoring-side weight tables verbatim — there is no shared
 * runtime import because dashboard/src cannot depend on lambdas/ (separate
 * workspace, per CLAUDE.md's "two forms" split). If either source changes,
 * update this table in the same PR:
 *   Delivery:  lambdas/bedrock-evaluator/src/evaluate.ts  DIMENSION_WEIGHTS (0–1 fractions × 100)
 *   Discovery: lambdas/bedrock-evaluator/src/discovery-rubric.ts  DISCOVERY_RUBRIC[].weight,
 *              re-normalized by lambdas/bedrock-evaluator/src/discovery-aggregator.ts's
 *              computeWeightedScore — see below.
 *
 * Discovery note: only 3 of the 6 Discovery dimensions are scored today
 * (DISCOVERY_RUBRIC's `scored !== false` filter); the other 3 render
 * "Coming Soon" and contribute 0 to the overall score. computeWeightedScore
 * divides by the SUM OF ONLY THE SCORED DIMENSIONS' WEIGHTS (20+20+10=50),
 * not by all 6 (100) — so the EFFECTIVE weight of each scored dimension
 * today is its raw weight ÷ 50, not ÷ 100:
 *   Discovery Planning Maturity: 20/50 = 40%
 *   Problem Framing Quality:     20/50 = 40%
 *   Discovery-to-Delivery Handoff: 10/50 = 20%
 * The 3 Coming Soon dimensions are deliberately OMITTED below (not given
 * their nominal 20/15/15) — showing a weight for a dimension that
 * contributes 0% to the score today would be misleading. Once a dimension
 * flips to `scored: true`, recompute every scored dimension's percent here
 * (the denominator changes for all of them, not just the newly-added one).
 */
export const DIMENSION_WEIGHT_PERCENT: Record<string, number> = {
  // Delivery — all 6 dimensions are scored; weights sum to 100.
  'constitution-maturity': 25,
  'artifact-traceability': 25,
  'supplemental-context': 15,
  'feedback-loop': 15,
  'ai-collaboration': 10,
  'framework-detection': 10,
  // Discovery — only the 3 scored dimensions; effective (re-normalized) weight.
  'Discovery Planning Maturity': 40,
  'Problem Framing Quality': 40,
  'Discovery-to-Delivery Handoff': 20,
};

export interface RubricLevel {
  level: string;
  text: string;
}

/**
 * Split a DIMENSION_RUBRICS entry ("L1: …\nL2: …") into {level, text} rows in
 * file order (L1 → L5). The "Lx:" prefix is stripped from the text because the
 * level is shown as a coloured badge instead.
 */
export function parseRubricLevels(rubric: string): RubricLevel[] {
  return rubric
    .split('\n')
    .map((line) => {
      const m = /^(L[1-5]):\s*(.*)$/.exec(line.trim());
      return m ? { level: m[1], text: m[2] } : null;
    })
    .filter((x): x is RubricLevel => x !== null);
}

/**
 * Leading lines of a DIMENSION_RUBRICS entry that come before the first "Lx:"
 * line — e.g. constitution-maturity's "judged on the practice, not the
 * filename" note. `parseRubricLevels` drops these (they don't match `^Lx:`),
 * so without this they never reach the UI at all.
 */
export function parseRubricPreamble(rubric: string): string[] {
  const lines = rubric.split('\n').map((line) => line.trim());
  const firstLevelIndex = lines.findIndex((line) => /^L[1-5]:/.test(line));
  const preambleLines = firstLevelIndex === -1 ? lines : lines.slice(0, firstLevelIndex);
  return preambleLines.filter((line) => line.length > 0);
}
